<script>
	import { mapState, mapGetters } from "vuex";

	import BaseTypeAhead from "../UI/BaseTypeAhead";
	import debounce from "vue-debounce";

	export default {
		components: {
			BaseTypeAhead
		},
		props: {
			id: { type: String, required: true },
			value: { type: String, required: false, default: "" },
			data: { type: Array, required: true },
			field: { type: String, required: true },
			placeholder: { type: String, required: true }
		},
		data() {
			return {
				focused: {
					input: false,
					suggestion: false
				},
				input: "",
				selected: false,
				$_debounceTimerT: null,
				isRequesting: false,
				draw: 0
			};
		},
		computed: {
			...mapGetters(["currency"]),
			...mapState({
				documentId: state => state.app.documentId
			}),
			filteredData() {
				const matchingRow = this.data.filter(row => {
					if (
						row?.id !== this.documentId &&
						row[this.field] &&
						row[this.field]?.toLowerCase().indexOf(this.input.toLowerCase()) != -1
					) {
						return row;
					}
				});

				if (!matchingRow.length) { // request search after a timeout
					this.debounce(async () => {
						this.isRequesting = true;
						this.draw++;
						
						const searchDone = await this.$store.dispatch("searchInvoice", { draw: this.draw, searchValue: this.input });
						
						this.isRequesting = false;
						
					}, 400);
					
				}
				
				return matchingRow;
			}
		},
		created() {
			this.input = this.value || "";
			this.$_debounceTimerT = null;
		},
		methods: {
			debounce(method, timer) {
				if (this.$_debounceTimerT !== null) {
					clearTimeout(this.$_debounceTimerT);
				}

				this.$_debounceTimerT = setTimeout(() => {
					method();
				}, timer);
			},
			complete(index) {
				if (typeof index === "undefined") {
					for (const row of this.data) {
						if (this.filter(row)) {
							this.select(row);

							return;
						}
					}
				}

				this.select(this.data[index]);
				this.focused.suggestion = false;
			},
			select(row) {
				this.input = row[this.field];
				this.selected = true;
				this.$emit("event-selected", { id: this.id, row });
			},
			filter(row) {
				return row[this.field]?.toLowerCase().indexOf(this.input.toLowerCase()) != -1;
			},
			focus(f) {
				this.focused = f;
				this.$refs.autocomplete.focus(f);
			},
			remove() {
				this.$emit("event-removeEntry", this.id);
			}
		}
	};
</script>

<template>
	<div
		ref="autocomplete"
		class="auto-complete p-0"
	>
		<BaseTypeAhead
			v-model="input"
			:list="filteredData"
			:field="'number'"
			:display-no-result="true"
			:display-on-focus="true"
			suggestion-list-class="suggestion__list"
			suggestion-item-class="suggestion__item"
			:is-loading="isRequesting"
			@select-item="select"
		>
			<template #input>
				<b-form-input
					v-model.lazy="input"
					type="text"
					class="billing__input pr-4"
					:placeholder="placeholder"
					@keydown.tab.prevent="complete"
					@focus="focused.input = true"
					@blur="focused.input = false"
				/>
				<b-icon
					icon="x"
					class="remove-entry"
					@click="remove"
				/>
			</template>

			<template #suggestion="suggestion">
				<span class="suggestion__name">#{{ suggestion.item.number }}</span>
				<span class="suggestion__TTC">{{ (suggestion.item.totals.due / 100) | convertNumberToString }}{{ currency }} {{ $t("TTC") }}</span>
				<span class="suggestion__subtitle text-muted">{{ suggestion.item.title }}</span>
				<span class="suggestion__customer text-muted">{{ suggestion.item.contact_infos.name }} - {{ suggestion.item.written_at | formatDate }}</span>
			</template>

			<template #noresult>
				<div class="suggestion__noResult">
					<p v-if="input.length > 0" class="suggestion__noResult__text">
						{{ isRequesting ? $t('Recherche en cours'): $t('Aucune facture ne correspond à votre recherche') }}
					</p>
					<p v-else class="suggestion__noResult__text">
						{{ $t("Aucune facture ne peut être ajoutée") }}
					</p>
				</div>
			</template>
		</BaseTypeAhead>
	</div>
</template>

<style lang="scss">
	.auto-complete {
		& .suggestion {
			position: absolute;
			min-width: 300px;
			width: max-content;
			max-width: 500px;
			max-height: 300px;
			box-shadow: 0 1px 6px rgba(119, 119, 119, 0.51);
			background-color: var(--white);
			border-radius: 3px;
			border-top: 0px;
			cursor: pointer;
			z-index: 10;
			padding: 5px 0;
			overflow: auto;
			scrollbar-color: transparent transparent;
			scrollbar-width: thin;
			@media screen and (prefers-reduced-motion: no-preference) {
				transition: scrollbar-color 300ms ease-in-out;
			}

			&:hover {
				scrollbar-color: #aeaeae transparent;
			}

			&__item {
				padding: 8px 15px;
				display: grid;
				grid-template-areas: "name TTC" "subtitle subtitle" "customer customer";
				grid-template-columns: 1f auto;

				&:hover {
					background-color: #f5f5f5;
				}
			}

			&__list {
				min-width: 300px;
				width: max-content;
				max-width: 500px;
			}

			&__name {
				grid-area: name;
			}

			&__TTC {
				grid-area: TTC;
				text-align: right;
			}

			&__subtitle {
				grid-area: subtitle;
			}

			&__customer {
				grid-area: customer;
			}

			&__noResult {
				padding: 0.5em 1em;

				&__text {
					margin: 0;
				}
			}

			& tr {
				background: var(--white);
			}

			& tr td {
				padding: 10px;
			}

			& tr:nth-child(even) {
				background: #eee;
			}

			& tr:hover {
				background: red;
				color: var(--white);
			}
		}
	}

	.b-icon.bi.remove-entry {
		position: absolute;
		top: 4px;
		right: 0px;
		font-size: 1.7rem;
		// color: var(--white);
		cursor: pointer;
	}
</style>
